import React from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { BuildingsIcon as Buildings, ShieldCheckIcon as ShieldCheck, BriefcaseIcon as Briefcase, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, WarningIcon as Warning, PrinterIcon as Printer, ArrowSquareOutIcon as ArrowSquareOut, ListChecksIcon as ListChecks, CurrencyDollarIcon as CurrencyDollar, ScalesIcon as Scales, QuestionIcon as Question, FilePdfIcon as FilePdf } from "@phosphor-icons/react";

/**
 * Admin → Business & Insurance Classification reference.
 *
 * A static, hand-curated reference document that captures how the
 * platform should describe itself on:
 *   - Insurance applications (where SaaS/marketplace dropdowns rarely fit)
 *   - Bank / merchant-account applications
 *   - Vendor / enterprise-customer security questionnaires
 *   - State business filings
 *
 * The page lives here so the operator can hand it to a broker, lawyer,
 * or banking partner in one URL and so the legal posture stays the same
 * doc no matter who's asking. Edit this file (not external Google Docs)
 * to keep the canonical statement under version control.
 *
 * NOT LEGAL ADVICE. This is the operator's working description of the
 * business; the lawyer / broker has the final word on how to file
 * anything externally.
 */

const ONE_LINER =
  "Operator of an online platform/website that lists purebred dog pedigrees, connects buyers with independent breeders and independent transporters, and processes payments via a third-party payment processor (Stripe). The platform does not breed, own, sell, or transport dogs and does not provide veterinary, breeding, or transportation services.";

const NAICS_RECOMMENDED = [
  {
    code: "519130",
    title: "Internet Publishing & Broadcasting and Web Search Portals",
    why: "Best primary fit. Covers content + directory + matchmaking + user-generated media (The Dog's Voice, Journal).",
    priority: "primary",
  },
  {
    code: "518210",
    title: "Data Processing, Hosting, and Related Services",
    why: "Strong secondary. Captures the SaaS / hosted-database nature of the registry + Dog Log.",
    priority: "primary",
  },
  {
    code: "454110",
    title: "Electronic Shopping and Mail-Order Houses",
    why: "Use when describing the Marketplace + Dog Shop side of the platform.",
    priority: "secondary",
  },
  {
    code: "541512",
    title: "Computer Systems Design Services",
    why: "Use if the form is biased toward 'tech services' framing.",
    priority: "secondary",
  },
  {
    code: "561499",
    title: "All Other Business Support Services",
    why: "Catch-all when nothing else fits — insurers and banks recognize this as the 'doesn't fit your dropdown' bucket.",
    priority: "fallback",
  },
];

const NAICS_AVOID = [
  {
    code: "488510",
    title: "Freight Transportation Arrangement",
    why: "Implies the platform itself is a freight broker. Triggers FMCSA scrutiny (MC Broker Authority, BMC-84 surety bond, BOC-3 process agents). The transporters using Armstrong Transport carry their own authority — the platform does not.",
  },
  {
    code: "812910",
    title: "Pet Care Services (except Veterinary)",
    why: "Implies the platform provides hands-on pet care or runs a kennel. Drags in animal-welfare liability the platform does not have — breeders and storefront sellers are the parties with that exposure.",
  },
  {
    code: "522320",
    title: "Financial Transactions Processing",
    why: "Implies the platform is the payment processor. Stripe is the processor. Using this code triggers fintech / money-services regulatory review the platform does not warrant.",
  },
  {
    code: "115210",
    title: "Support Activities for Animal Production",
    why: "Implies the platform is an agricultural services provider. The platform does not breed, raise, transport, or sell animals.",
  },
];

const WHAT_WE_ARE = [
  "An online directory and database of purebred dog pedigrees",
  "A two-sided marketplace connecting buyers with independent breeders",
  "A two-sided marketplace connecting buyers with independent vetted transporters",
  "A multi-tenant e-commerce platform allowing kennels to sell food and merchandise from their own pages",
  "A subscription SaaS for daily dog-care tracking (Dog Log)",
  "A user-generated content site (The Dog's Voice, Journal)",
  "A facilitator of payments via Stripe Connect — Stripe is the merchant of record",
];

const WHAT_WE_ARE_NOT = [
  "We do not breed, raise, own, board, or care for any dog",
  "We do not buy, sell, broker, or take title to any dog",
  "We do not transport, drive, or take custody of any dog",
  "We do not manufacture, fulfill, ship, or store any product sold through Dog Shop storefronts",
  "We do not provide veterinary, medical, or behavioral advice",
  "We do not provide breeding, mating, or genetics consulting",
  "We do not provide transportation, freight, or logistics-brokerage services",
  "We do not custody buyer funds — Stripe holds escrow funds until release conditions are met",
  "We do not insure dogs, transactions, transporters, breeders, or storefronts",
];

const INSURANCE_NEEDS = [
  {
    coverage: "Technology Errors & Omissions (Tech E&O) / Cyber Liability",
    rationale:
      "Primary coverage for any SaaS/marketplace. Covers software defects, data breaches, IP claims, content liability, and platform errors that cause financial loss to users.",
    typical: "$1,200 – $3,500 / yr",
    priority: "required",
  },
  {
    coverage: "General Liability (CGL)",
    rationale:
      "Required by most B2B contracts and many state business licenses even when the business has no physical premises. Covers slip-and-fall, property damage, and third-party bodily-injury claims.",
    typical: "$400 – $800 / yr",
    priority: "required",
  },
  {
    coverage: "Media / Content Liability",
    rationale:
      "Covers claims arising from user-generated content (The Dog's Voice posts, Journal articles, reviews, kennel descriptions). Often bundled inside Tech E&O.",
    typical: "Bundled with Tech E&O",
    priority: "required",
  },
  {
    coverage: "Directors & Officers (D&O)",
    rationale:
      "Protects founders / officers personally from claims brought against the business. Becomes important when raising outside capital, hiring employees, or adding board members.",
    typical: "$1,500 – $4,000 / yr",
    priority: "later",
  },
  {
    coverage: "Employment Practices Liability (EPLI)",
    rationale: "Only relevant once the business has W-2 employees.",
    typical: "Defer until first hire",
    priority: "later",
  },
];

const INSURANCE_NOT_NEEDED = [
  {
    coverage: "Motor truck cargo / Freight broker bond (BMC-84)",
    why: "Each independent transporter on Armstrong Transport carries their own MC authority, USDOT number, and commercial-auto liability ($300K minimum). The platform is not the carrier or the broker.",
  },
  {
    coverage: "Product liability",
    why: "Products are sold under the Storefront Seller Agreement, which makes the kennel the merchant of record. Stripe Connect destination charges enforce this — the seller (not the platform) is paid for the goods.",
  },
  {
    coverage: "Veterinary malpractice",
    why: "The platform does not practice veterinary medicine.",
  },
  {
    coverage: "Animal mortality / Livestock insurance",
    why: "The platform does not own any animals.",
  },
];

const MARKETPLACE_BROKERS = [
  {
    name: "Vouch",
    url: "https://www.vouch.us",
    note: "Built specifically for tech / SaaS / marketplace startups. Will quote without forcing a wrong-fit category.",
  },
  {
    name: "Embroker",
    url: "https://www.embroker.com",
    note: "Tech-focused. 'Startup Package' bundles Tech E&O + GL + D&O.",
  },
  {
    name: "Hiscox",
    url: "https://www.hiscox.com",
    note: "Recognizes 'online retailer' / 'professional services online' categories without trying to re-bucket the business.",
  },
  {
    name: "Coalition",
    url: "https://www.coalitioninc.com",
    note: "Strong cyber + Tech E&O specifically. Weaker on GL.",
  },
  {
    name: "Founder Shield",
    url: "https://foundershield.com",
    note: "Best fit once at Series A+ or 10+ employees.",
  },
];

const INSURANCE_ANSWER_KEY = [
  {
    section: "Business Activities",
    rows: [
      { q: "Average contract / transaction size", a: "$50–150", note: "Most users on monthly subscriptions ($4.99–$44). This is your contract with the user, NOT marketplace gross volume." },
      { q: "Largest contract / transaction size", a: "$2,499", note: "Kennel Elite 2-year prepay (V2 pricing, launched 2026-05-27) is the largest single payment from one user." },
      { q: "Average contract length", a: "1 month", note: "Most users on monthly subscriptions." },
      { q: "Largest contract length", a: "12 months", note: "Annual subscriptions." },
      { q: "Largest customers (top 3 by revenue)", a: "N/A — DTC / SMB platform", note: "No individual customer >5% of revenue. If form requires names, list actual top 3 paying kennels — confidential and lowers premium (no concentration risk)." },
    ],
  },
  {
    section: "Contract Management",
    rows: [
      { q: "Use independent contractors?", a: "Yes", note: "For back-office (designers, accountants, lawyers). NOT a reference to marketplace breeders/transporters." },
      { q: "Always receive sign-off from end client on deliverables?", a: "No", note: "Doesn't apply — self-serve SaaS, no per-client deliverables." },
      { q: "Formal customer-complaint process?", a: "Yes", positive: true, note: "/contact, in-app inbox, published mutual-consent + 30-day-default refund policy, admin moderation queue." },
      { q: "Work under written contract signed by every client?", a: "Yes", note: "Click-through Terms, Privacy, Storefront/Transport agreements at signup." },
      { q: "Assume liability for others under your contracts?", a: "No", critical: true, note: "Terms explicitly disclaim liability for breeders/transporters/sellers. They are independent parties." },
      { q: "Fees contingent on client achieving cost reductions?", a: "No", note: "Fixed subscriptions + flat % transaction fees. None tied to client savings." },
      { q: "Use written contract describing services?", a: "Yes", note: "ToS, Privacy, Seller Agreement, Transport Agreement." },
    ],
  },
  {
    section: "Contract Inclusions (check all that apply)",
    rows: [
      { q: "Disclaimer or Warranties", a: "✓ Check", note: "AS IS, no warranties — Terms." },
      { q: "Statement of Work", a: "✗ Skip", note: "Not consulting / no per-project SOWs." },
      { q: "Hold Harmless to your benefit", a: "✓ Check", note: "Seller Agreement §5 + Terms — users indemnify the platform." },
      { q: "Hold Harmless to clients' benefit", a: "✗ Skip", note: "Never agree to indemnify users (standard SaaS posture)." },
      { q: "Exclusions / Disclaimer of Consequential Damages", a: "✓ Check", note: "Standard limitation language in Terms." },
      { q: "Limitation of Liability", a: "✓ Check", note: "Standard in Terms." },
    ],
  },
  {
    section: "Records — what data do you collect?",
    rows: [
      { q: "Personal Information", a: "Yes", note: "Name, email, address, phone, dog records, kennel info." },
      { q: "Payment Card Information", a: "No", critical: true, note: "CRITICAL. Stripe handles all card data. You never see, store, or process card numbers. Saying Yes triggers PCI-DSS review and a much higher premium." },
      { q: "Healthcare Records", a: "No", note: "Dog health records ≠ HIPAA-covered records." },
      { q: "Biometric Information", a: "No", note: "No fingerprints, face scans, etc. Dog photos don't count." },
    ],
  },
  {
    section: "Network & Data Security",
    rows: [
      { q: "Encryption on laptops/desktops?", a: "Yes", note: "FileVault (Mac) and BitLocker (Windows 11) are on by default. Verify enabled." },
      { q: "Security Operations Center (SOC)?", a: "No", note: "Solo founder — no SOC. Honest." },
      { q: "Pre-screen emails for malicious attachments/links?", a: "Yes", note: "Google Workspace / Outlook 365 do this by default." },
      { q: "Sandbox-detonate attachments before delivery?", a: "No", note: "Enterprise feature (Defender for Office 365 Plan 2, Proofpoint TAP, Mimecast, Workspace Enterprise w/ Security Sandbox). Don't say Yes unless you pay for one of these — a claim audit will catch it." },
      { q: "Email security software used", a: "Google Workspace (built-in)", note: "If 'Google Workspace' isn't a checkbox, select 'None of the above' and add a comment: Google Workspace Business Standard — relies on native attachment scanning, phishing detection, SPF/DKIM/DMARC. Do NOT check Microsoft Defender unless you're on paid M365." },
      { q: "Next-Generation Antivirus (NGAV)?", a: "No", note: "Unless you have CrowdStrike / SentinelOne / Defender for Business, default to No." },
      { q: "Endpoint Detection and Response (EDR)?", a: "No", note: "Enterprise tool — not at your size." },
      { q: "Bring Your Own Device (BYOD)?", a: "Yes", note: "Personal laptop. Fine." },
      { q: "Vulnerability management tool?", a: "No", note: "Platform infra managed by Emergent (PaaS) which handles patching." },
      { q: "Remote access to CLIENT network via RMM/VPN?", a: "No", critical: true, note: "CRITICAL. This asks if you VPN INTO customers' networks like an MSP. You're SaaS — customers come to you. Saying Yes adds MSP-class liability." },
      { q: "Procedure for editing/removing libelous or IP-infringing content?", a: "Yes", note: "Admin moderation queue, /contact, DMCA contact in Terms." },
      { q: "Use material provided by others (content / music / graphics / video)?", a: "Yes", note: "Be honest — licensed stock photos, open-source libraries, user-uploaded content." },
      { q: "Email service", a: "Google Workspace", note: "Or whichever you actually use." },
    ],
  },
  {
    section: "MFA & Backups",
    rows: [
      { q: "Allow remote access to your network?", a: "No", note: "No internal corporate network. Network = the public SaaS platform itself." },
      { q: "Use Multi-Factor Authentication (MFA)?", a: "Yes", positive: true, note: "Admin login, Stripe, GitHub, Google all 2FA-enabled. Platform also offers TOTP 2FA to users via PyOTP." },
      { q: "MFA provider?", a: "Google Authenticator (TOTP)", note: "Multi-layer: Google Authenticator for personal admin logins (Stripe, GitHub, Google), Google 2-Step Verification for Workspace, PyOTP-based TOTP for platform users. If form forces one choice, 'Google Authenticator' or 'Authenticator app' is correct." },
      { q: "Use a data backup solution?", a: "Yes", note: "MongoDB backups via Emergent + code on GitHub + Stripe transaction records exportable from dashboard + Cloudflare R2 for media assets." },
      { q: "Access to backups?", a: "Yes — Founder only", positive: true, note: "Single-admin org. Minimum-privilege access. Underwriters like seeing this." },
    ],
  },
  {
    section: "Money Transfers",
    rows: [
      { q: "Send/receive wire transfers?", a: "No", critical: true, note: "CRITICAL. Stripe payouts to bank are ACH, not wires. Skips the wire-fraud risk category entirely. If form forces sub-questions on Yes, answer Yes on all (you'd follow best practices if you ever wired)." },
      { q: "Cyber security awareness program with phishing training & testing?", a: "No", note: "Solo founder, no formal program. Fine." },
      { q: "Network security is:", a: "Managed in-house", note: "You + Emergent's platform-level controls. If 'Outsourced to PaaS' is an option, that's even better." },
    ],
  },
  {
    section: "Cloud Providers & IT",
    rows: [
      { q: "Cloud providers you DIRECTLY depend on?", a: "None of the above", critical: true, note: "Do NOT check AWS/GCP/Azure unless you have a direct account. You're on Emergent (Cloudflare-backed PaaS). Note this in any comments field." },
      { q: "Responsible for network security (Name)", a: "Lauren Armstrong (or your name)" },
      { q: "Responsible for network security (Title)", a: "Founder / Owner" },
      { q: "IT Security Designation(s)", a: "N/A — solo founder. Platform infrastructure managed by Emergent (PaaS). Platform-level security controls documented separately." },
      { q: "How many IT personnel?", a: "1 (or 0)" },
      { q: "Use a Managed Service Provider (MSP)?", a: "No", critical: true, note: "CRITICAL. Emergent is a PaaS (platform-as-a-service), NOT an MSP. MSP in insurance language means a third-party IT firm managing your office endpoints/networks." },
    ],
  },
  {
    section: "Claims History",
    rows: [
      { q: "Experienced claims, or aware of incidents that could give rise to claims?", a: "No", critical: true, note: "Be 100% honest. Lying here voids the entire policy. Disclose even informal legal threats." },
    ],
  },
  {
    section: "Primary technology business — Software / Hardware / Services",
    rows: [
      { q: "Software?", a: "Yes", note: "SaaS platform — Armstrong K9 Registry is code running in browsers." },
      { q: "Hardware?", a: "No", note: "Don't manufacture, sell, or service physical devices." },
      { q: "Services?", a: "No", critical: true, note: "CRITICAL. 'Services' in insurance language = professional services (consulting / dev-for-hire / advisory). Even though SaaS stands for 'Software AS A Service,' checking this opens you to Professional Services E&O scrutiny — higher premiums. If asked, clarify: SaaS subscription access to self-serve software, no consulting." },
    ],
  },
  {
    section: "Business Operations checklist (huge industry list)",
    rows: [
      { q: "E-commerce", a: "✓ Check", positive: true, note: "Dog Shop + Marketplace + Armstrong Transport all involve commerce transactions." },
      { q: "Social Networking, Social Media and User Generated Content Websites", a: "✓ Check", positive: true, note: "The Dog's Voice, Journal, kennel profiles, reviews, photos." },
      { q: "Custom computer programming / software development", a: "✗ Skip", note: "You build software for yourself, not for paying clients. This category is for dev shops." },
      { q: "Cyber security / IT security / identity protection services", a: "✗ Skip", note: "You implement security; you don't sell it as a service." },
      { q: "Consumer data mining / broker / VPN / domain registry", a: "✗ Skip", critical: true, note: "Saying Yes triggers data-broker scrutiny under state privacy laws and heightened cyber rates." },
      { q: "Content Delivery Platforms", a: "✗ Skip", note: "You use a CDN (Cloudflare). You don't operate one." },
      { q: "Health (trackers, monitors, caregiving) / Healthcare advice / Medical *", a: "✗ Skip", note: "These all refer to HUMAN health. Dog Log + pet care don't trigger HIPAA scrutiny." },
      { q: "Open Source Software/Code", a: "✗ Skip", note: "You use open-source; you don't sell or distribute an OSS product." },
      { q: "Point of sale integration", a: "✗ Skip", note: "Stripe is the POS — not you." },
      { q: "Sharing economy platforms", a: "✗ Skip", note: "Tempting (Armstrong Transport has Uber vibes), but stay consistent with marketplace framing — avoids TNC regulatory baggage." },
      { q: "Relationship / dating apps / Online Dating", a: "✗ Skip", note: "Breeding directory is dog matchmaking — categorically different from human dating (higher harassment/safety claim rates)." },
      { q: "Live streaming", a: "✗ Skip", note: "Daily.co video tours are too narrow to qualify." },
      { q: "Training", a: "✗ Skip", note: "Don't sell training as a service." },
      { q: "Publishers / Copywriting / Media Rights Management", a: "✗ Skip", note: "The Journal is editorial content for your own platform, not a publishing business." },
      { q: "Loyalty Programs / Rewards", a: "✗ Skip", note: "Buyer Pass is a subscription, not a points/rewards program." },
      { q: "Financial *, Crowdfunding, POS, Banking, Trading, Investment Advisory", a: "✗ Skip", critical: true, note: "All No. Stripe is the payment processor — you're not in fintech. Saying Yes invokes financial-services E&O which is 5–10× the premium." },
      { q: "Drone, Aviation, Autonomous Vehicles, Military, Flight Control, Medical Devices", a: "✗ Skip", note: "None apply. Don't be tempted by 'almost' fits." },
      { q: "None of the above", a: "✗ Skip", note: "You have two valid selections (e-commerce + UGC)." },
    ],
  },
  {
    section: "Backup security details",
    rows: [
      { q: "Backups encrypted?", a: "Yes", note: "MongoDB managed backups + GitHub repos are encrypted at rest. Confirm with Emergent support before submitting if you want to be 100% sure." },
      { q: "Immutable backups?", a: "No", note: "'Immutable' = ransomware-proof, write-once (e.g., AWS S3 Object Lock, Rubrik). Most managed Mongo backups aren't truly immutable. Don't fake it — audits check." },
      { q: "Backups secured with different credentials than other admin credentials?", a: "Yes", positive: true, note: "Emergent platform login, MongoDB direct access, GitHub, and Stripe each have separate credentials with 2FA. Underwriters like this." },
      { q: "Tested restoration from backups in last 6 months?", a: "No (honestly)", note: "Most small ops haven't. If you want this Yes, do a test restore this week (restore Mongo backup to staging DB, verify it loads). Worth doing regardless." },
      { q: "Can test integrity of backups before restoration (malware-free)?", a: "No", note: "Enterprise-grade feature; solo founder doesn't have it." },
      { q: "How frequently are backups run?", a: "Daily", note: "Standard for managed Mongo. Verify with Emergent — if their console shows continuous PITR, choose 'Continuous / PITR'." },
      { q: "Estimated restore time after ransomware?", a: "4–24 hours", note: "Realistic for a single-engineer team. Don't claim <1h (no hot standby) or >7d (looks like no plan). 4–24h is the honest middle." },
    ],
  },
];

const ANSWER_KEY_CRITICAL = [
  "Payment Card Information → No (Stripe handles it)",
  "Remote access to client networks via RMM/VPN → No (you're SaaS, not MSP)",
  "Send/receive wire transfers → No (ACH only)",
  "Use a Managed Service Provider → No (Emergent is PaaS, not MSP)",
  "Assume liability for others under contracts → No (Terms disclaim it)",
  "Cloud providers direct dependency → None of the above (Emergent is your stack)",
];



function Status({ kind, children }) {
  const cls =
    kind === "good"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : kind === "warn"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-rose-50 text-rose-800 border-rose-200";
  const Icon = kind === "good" ? CheckCircle : kind === "warn" ? Warning : XCircle;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${cls}`}
    >
      <Icon size={11} weight="fill" />
      {children}
    </span>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title, intro }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} weight="duotone" className="text-[#8A6A00]" />
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">{eyebrow}</p>
      </div>
      <h2 className="font-spectral font-light text-2xl md:text-3xl tracking-tight text-ink">{title}</h2>
      {intro && (
        <p className="text-[13px] text-ink/65 leading-relaxed mt-2 max-w-3xl">{intro}</p>
      )}
    </div>
  );
}

export default function AdminBusinessClassification() {
  return (
    <AppShell>
      <div
        className="max-w-5xl mx-auto px-6 py-8 print:py-2"
        data-testid="admin-business-classification"
      >
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4 print:hidden">
          <div>
            <Link
              to="/admin"
              className="text-[12px] text-ink/55 hover:text-ink inline-flex items-center gap-1"
            >
              ← Admin Console
            </Link>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mt-3 mb-1">
              Operations · Legal posture
            </p>
            <h1 className="font-spectral font-light text-4xl md:text-5xl tracking-tight text-ink">
              Business &amp; Insurance Classification
            </h1>
            <p className="text-ink/65 mt-3 text-base leading-relaxed max-w-2xl">
              A reference for filling out insurance, banking, and vendor
              applications. Hand the URL (or print the page) to brokers and
              underwriters so the platform gets categorized correctly the
              first time — not pattern-matched into a wrong-fit dropdown.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="rounded-full h-10 border-stone-300"
            data-testid="business-classification-print"
          >
            <Printer size={14} weight="duotone" className="mr-1.5" />
            Print / Save PDF
          </Button>
        </div>

        {/* Not legal advice */}
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 mb-10 text-[12.5px] text-amber-900 leading-relaxed">
          <strong className="block mb-1">Not legal advice.</strong>
          This page is the operator's working description of the business and
          how it should be classified on external forms. Your attorney,
          insurance broker, and accountant have the final word on how to file
          anything externally — especially when scaling Armstrong Transport
          beyond a small pilot.
        </div>

        {/* The one-liner */}
        <section className="mb-12" data-testid="bc-one-liner">
          <SectionHeader
            icon={Briefcase}
            eyebrow="The one-sentence description"
            title="Use this exact sentence on any 'describe your business' field"
            intro="Every clause is load-bearing. The verbs ('lists', 'connects', 'processes via a third-party') keep the platform on the marketplace/SaaS side of the line. The explicit carve-outs at the end block the worst claim categories from attaching to the wrong party."
          />
          <div className="rounded-2xl bg-stone-50 border-2 border-stone-200 p-6">
            <p className="font-spectral text-lg md:text-xl text-ink leading-relaxed italic">
              "{ONE_LINER}"
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-4 rounded-full text-[12px] h-8"
              onClick={() => {
                navigator.clipboard?.writeText(ONE_LINER);
              }}
              data-testid="bc-copy-one-liner"
            >
              Copy to clipboard
            </Button>
          </div>
        </section>

        {/* What we are / are not */}
        <section className="mb-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="bc-scope">
          <div className="rounded-2xl border-2 border-emerald-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={18} weight="duotone" className="text-emerald-700" />
              <h3 className="font-spectral text-xl tracking-tight text-ink">What we are</h3>
            </div>
            <ul className="space-y-2">
              {WHAT_WE_ARE.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-2 text-[13px] text-ink/80 leading-snug"
                >
                  <CheckCircle size={13} weight="fill" className="text-emerald-600 mt-1 shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-rose-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-3">
              <XCircle size={18} weight="duotone" className="text-rose-700" />
              <h3 className="font-spectral text-xl tracking-tight text-ink">What we are NOT</h3>
            </div>
            <ul className="space-y-2">
              {WHAT_WE_ARE_NOT.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-2 text-[13px] text-ink/80 leading-snug"
                >
                  <XCircle size={13} weight="fill" className="text-rose-600 mt-1 shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* NAICS Codes */}
        <section className="mb-12" data-testid="bc-naics">
          <SectionHeader
            icon={ListChecks}
            eyebrow="NAICS / business codes"
            title="What to enter when the form asks for an industry code"
            intro="Most insurance + banking forms still use NAICS. The codes below are ordered by how cleanly they fit. When asked for a single primary code, use 519130. When asked for additional secondary activities, layer 518210 and 454110."
          />
          <div className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.25em] text-ink/55 mt-4 mb-1">
              ✓ Recommended
            </h3>
            {NAICS_RECOMMENDED.map((n) => (
              <div
                key={n.code}
                className="rounded-xl border border-stone-200 bg-white p-4 flex flex-col sm:flex-row gap-4"
              >
                <div className="sm:w-32 shrink-0">
                  <p className="font-tabular text-lg text-ink font-semibold">{n.code}</p>
                  <Status kind={n.priority === "primary" ? "good" : n.priority === "secondary" ? "warn" : "warn"}>
                    {n.priority}
                  </Status>
                </div>
                <div>
                  <p className="font-semibold text-ink text-[14px]">{n.title}</p>
                  <p className="text-[12.5px] text-ink/65 leading-relaxed mt-1">{n.why}</p>
                </div>
              </div>
            ))}
            <h3 className="text-[11px] font-bold uppercase tracking-[0.25em] text-rose-700 mt-6 mb-1">
              ✗ Avoid — wrong fit
            </h3>
            {NAICS_AVOID.map((n) => (
              <div
                key={n.code}
                className="rounded-xl border border-rose-200 bg-rose-50/30 p-4 flex flex-col sm:flex-row gap-4"
              >
                <div className="sm:w-32 shrink-0">
                  <p className="font-tabular text-lg text-rose-900 font-semibold">{n.code}</p>
                  <Status kind="bad">avoid</Status>
                </div>
                <div>
                  <p className="font-semibold text-rose-900 text-[14px]">{n.title}</p>
                  <p className="text-[12.5px] text-ink/70 leading-relaxed mt-1">{n.why}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why not "Logistics Management" */}
        <section className="mb-12" data-testid="bc-logistics-clarification">
          <SectionHeader
            icon={Scales}
            eyebrow="A specific category to avoid"
            title="Why &lsquo;Logistics Management&rsquo; is the wrong classification"
            intro="The Logistics Management category specifically describes a consulting / operations-management role: an advisor hired by a client to plan and optimize that client's supply chain. The platform does none of that — it connects independent parties and processes payments. Misclassifying here could be treated as misrepresentation if a claim is filed."
          />
          <div className="rounded-2xl bg-white border-2 border-amber-200 p-5">
            <p className="text-[12.5px] text-ink/75 leading-relaxed">
              <strong className="text-ink">Logistics Management (per most insurer definitions):</strong>{" "}
              &ldquo;Logistics managers provide advice for clients looking to reduce
              costs or improve logistical performance. They focus on improving
              the supply of goods/services to better meet customer needs. To do
              this, they plan, organize, and manage activities.&rdquo;
            </p>
            <p className="text-[12.5px] text-ink/75 leading-relaxed mt-3">
              <strong className="text-ink">Why it doesn&rsquo;t fit:</strong> The
              verbs &mdash; <em>advise</em>, <em>plan</em>, <em>organize</em>, <em>manage</em> &mdash;
              describe an advisory or operational role on behalf of a client.
              Armstrong K9 Registry has no logistics-management clients; it
              lists independent transporters and lets buyers and transporters
              contract directly. The platform never plans routes, dispatches
              loads, optimizes anyone&rsquo;s supply chain, or sells advisory
              services. Filing under this category risks a denial under
              &ldquo;misrepresentation on application&rdquo; if a claim ever
              touches Armstrong Transport.
            </p>
          </div>
        </section>

        {/* Insurance needs */}
        <section className="mb-12" data-testid="bc-insurance">
          <SectionHeader
            icon={ShieldCheck}
            eyebrow="Coverage we actually need"
            title="What to request from a broker"
            intro="A SaaS/marketplace insurance stack is much narrower than what generalist brokers try to upsell. The four lines below are the spine. Everything past that is either premature (D&O before raising), redundant (product liability when storefront sellers are merchants of record), or someone else's problem (motor truck cargo on the transporters)."
          />
          <div className="space-y-3">
            {INSURANCE_NEEDS.map((c) => (
              <div
                key={c.coverage}
                className="rounded-xl border border-stone-200 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <p className="font-semibold text-ink text-[14px]">{c.coverage}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[11.5px] text-ink/55 font-tabular">{c.typical}</span>
                    <Status kind={c.priority === "required" ? "good" : "warn"}>
                      {c.priority}
                    </Status>
                  </div>
                </div>
                <p className="text-[12.5px] text-ink/70 leading-relaxed">{c.rationale}</p>
              </div>
            ))}
          </div>

          <h3 className="text-[11px] font-bold uppercase tracking-[0.25em] text-rose-700 mt-6 mb-1">
            ✗ Don&rsquo;t let a broker upsell these
          </h3>
          <div className="space-y-3">
            {INSURANCE_NOT_NEEDED.map((c) => (
              <div
                key={c.coverage}
                className="rounded-xl border border-rose-200 bg-rose-50/30 p-4"
              >
                <p className="font-semibold text-rose-900 text-[13.5px]">{c.coverage}</p>
                <p className="text-[12px] text-ink/70 leading-relaxed mt-1">{c.why}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Brokers list */}
        <section className="mb-12" data-testid="bc-brokers">
          <SectionHeader
            icon={Buildings}
            eyebrow="Brokers / carriers that quote marketplaces"
            title="Where to get quotes without fighting a wrong-fit dropdown"
            intro="Avoid traditional regional agencies for first quotes — they default to pet/retail/transport categories. The shops below understand SaaS + marketplace natively."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {MARKETPLACE_BROKERS.map((b) => (
              <a
                key={b.name}
                href={b.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold transition-colors"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="font-semibold text-ink">{b.name}</p>
                  <ArrowSquareOut size={13} className="text-ink/40" />
                </div>
                <p className="text-[12.5px] text-ink/65 leading-relaxed">{b.note}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Insurance application answer key */}
        <section className="mb-12" data-testid="bc-answer-key">
          <SectionHeader
            icon={Question}
            eyebrow="Insurance application cheat sheet"
            title="Answer key for E&amp;O / cyber / GL applications"
            intro="Common questions that trip up SaaS/marketplaces. The first time you fill out an E&O or cyber form, these answers will save you guessing — and saying the wrong thing here can balloon your premium or void coverage. Critical answers are flagged in red; positive signals (underwriters like seeing these) are flagged in green."
          />

          <div className="rounded-2xl bg-rose-50 border-2 border-rose-200 p-5 mb-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-rose-800 mb-2">
              The 6 critical-to-get-right answers
            </p>
            <ul className="space-y-1.5">
              {ANSWER_KEY_CRITICAL.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-[12.5px] text-rose-900 leading-snug">
                  <Warning size={13} weight="fill" className="text-rose-700 mt-0.5 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11.5px] text-rose-900/80 mt-3 italic">
              Each of these answered correctly likely saves $200–$500+/yr in premium because it removes a major risk category from the underwriter&rsquo;s pricing model.
            </p>
          </div>

          <div className="space-y-6">
            {INSURANCE_ANSWER_KEY.map((sec) => (
              <div
                key={sec.section}
                className="rounded-2xl border border-stone-200 bg-white overflow-hidden"
              >
                <div className="bg-stone-50 px-5 py-3 border-b border-stone-200">
                  <h3 className="font-spectral text-lg tracking-tight text-ink">{sec.section}</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {sec.rows.map((row, i) => (
                    <div key={i} className="px-5 py-3.5 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                      <div className="md:col-span-6">
                        <p className="text-[13px] text-ink/85 leading-snug">{row.q}</p>
                      </div>
                      <div className="md:col-span-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-[12px] font-bold border ${
                            row.critical
                              ? "bg-rose-50 text-rose-900 border-rose-300"
                              : row.positive
                              ? "bg-emerald-50 text-emerald-900 border-emerald-300"
                              : "bg-stone-50 text-ink border-stone-300"
                          }`}
                        >
                          {row.a}
                        </span>
                      </div>
                      {row.note && (
                        <div className="md:col-span-4">
                          <p className={`text-[11.5px] leading-snug ${row.critical ? "text-rose-800 font-medium" : "text-ink/55"}`}>
                            {row.note}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>


        {/* Sample customer contract for vendor / insurance submissions */}
        <section className="mb-12" data-testid="bc-sample-contract">
          <SectionHeader
            icon={FilePdf}
            eyebrow="When a form asks for 'sample contract used with customers'"
            title="Your customer contract = Terms of Service"
            intro="For a SaaS/marketplace, the click-through Terms users accept at signup IS the binding customer contract. Click-through ToS is legally enforceable in the US (Specht v. Netscape Communications, Nguyen v. Barnes & Noble, etc.). Each legal page below has a Print / Save PDF button at the top."
          />
          <div className="rounded-2xl bg-stone-50 border border-stone-200 p-5 mb-5">
            <p className="text-[12.5px] text-ink/75 leading-relaxed mb-3">
              <strong className="text-ink">How to generate the PDF for an insurance / vendor submission:</strong>
            </p>
            <ol className="text-[12.5px] text-ink/75 leading-relaxed list-decimal pl-5 space-y-1.5">
              <li>Open the legal page that matches what the form is asking for (Terms of Service for most cases).</li>
              <li>Click the &ldquo;Print / Save PDF&rdquo; button in the top-right of the page.</li>
              <li>In the print dialog, choose &ldquo;Save as PDF&rdquo; as the destination.</li>
              <li>Attach the PDF to the application.</li>
            </ol>
            <p className="text-[12px] text-ink/55 italic mt-3">
              The PDF strips navigation, the footer, and the sticky header — it&rsquo;s just the contract text.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="rounded-xl border-2 border-emerald-200 bg-white p-4 hover:border-emerald-400 transition-colors"
              data-testid="bc-link-terms"
            >
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-ink text-[14px]">Terms of Service</p>
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  Primary
                </span>
              </div>
              <p className="text-[12px] text-ink/65 leading-relaxed">
                The main customer contract. Covers eligibility, accounts, marketplace disclaimers, escrow, arbitration, IP, DMCA, termination, payments, and Storefront platform fee. <strong>Use this 90% of the time.</strong>
              </p>
            </a>
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold transition-colors"
            >
              <p className="font-semibold text-ink text-[14px] mb-1">Privacy Policy</p>
              <p className="text-[12px] text-ink/65 leading-relaxed">
                Attach alongside Terms when the form also asks for &ldquo;privacy notice&rdquo; or &ldquo;data handling agreement.&rdquo;
              </p>
            </a>
            <a
              href="/storefront-seller-agreement"
              target="_blank"
              rel="noreferrer"
              className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold transition-colors"
            >
              <p className="font-semibold text-ink text-[14px] mb-1">Storefront Seller Agreement</p>
              <p className="text-[12px] text-ink/65 leading-relaxed">
                The contract Dog Shop subscribers click-accept. Attach when the underwriter specifically asks about merchant-of-record arrangements or Stripe Connect.
              </p>
            </a>
            <a
              href="/refund"
              target="_blank"
              rel="noreferrer"
              className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold transition-colors"
            >
              <p className="font-semibold text-ink text-[14px] mb-1">Refund Policy</p>
              <p className="text-[12px] text-ink/65 leading-relaxed">
                Often requested for e-commerce / cyber applications.
              </p>
            </a>
          </div>

          <div className="mt-5 rounded-xl border-2 border-amber-200 bg-amber-50/50 p-4">
            <p className="text-[12.5px] text-amber-900 leading-relaxed">
              <strong>Heads-up on the draft banner:</strong> The Terms of Service
              page currently displays a banner noting the document is a working
              draft pending final attorney review. This banner prints with the
              PDF. For early-stage insurance applications underwriters typically
              accept this with a note like <em>&ldquo;live customer agreement,
              attorney review pending&rdquo;</em>. If the underwriter pushes
              back, finalize attorney review first &mdash; that&rsquo;s the
              cleaner long-term move and removes the banner.
            </p>
          </div>
        </section>



        {/* Armstrong Transport caveat */}
        <section className="mb-12" data-testid="bc-armstrong-transport">
          <SectionHeader
            icon={CurrencyDollar}
            eyebrow="Special consideration"
            title="Armstrong Transport — the one piece that needs a separate look"
            intro="The transport marketplace is the one module that brushes against FMCSA / transportation-brokerage territory. The other modules sit cleanly inside the marketplace / SaaS umbrella."
          />
          <div className="rounded-2xl border-2 border-stone-200 bg-white p-6 space-y-3 text-[13px] text-ink/75 leading-relaxed">
            <p>
              <strong className="text-ink">Current legal posture:</strong>{" "}
              Armstrong Transport is a marketplace. Independent transporters
              hold their own MC authority, USDOT number, $300K minimum
              commercial-auto liability, and (where applicable) USDA Class B
              license. They contract directly with buyers; the platform
              facilitates discovery, payment routing, and the published
              refund timeline only — Armstrong does NOT adjudicate the
              merits of disputes between buyer and transporter.
            </p>
            <p>
              <strong className="text-ink">When to revisit:</strong> Before
              Armstrong Transport scales past roughly $10K/month in GMV,
              consult a transportation lawyer on whether the platform itself
              needs MC Broker Authority (FMCSA), a BMC-84 surety bond ($75,000),
              and BOC-3 process agents in each state. One $500–1,500
              consultation answers this cleanly.
            </p>
            <p>
              <strong className="text-ink">Optional separation play:</strong>{" "}
              Long-term, Armstrong Transport could spin into a sibling LLC
              (and even a sibling brand at <em>armstrongtransport.com</em>)
              with its own broker authority. That cleanly insulates the
              registry from any FMCSA exposure and lets the transport
              business scale into a regulated regime without dragging the
              SaaS side with it.
            </p>
          </div>
        </section>

        {/* Internal references */}
        <section className="mb-16 print:hidden" data-testid="bc-cross-refs">
          <SectionHeader
            icon={ListChecks}
            eyebrow="Related admin pages"
            title="Where the supporting evidence lives in the platform"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link
              to="/admin/security"
              className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold transition-colors"
            >
              <p className="font-semibold text-ink text-[14px]">Security &amp; compliance</p>
              <p className="text-[12px] text-ink/65 mt-1">
                Printable inventory of every security control — hand to an underwriter.
              </p>
            </Link>
            <Link
              to="/admin/legal-bundle"
              className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold transition-colors"
            >
              <p className="font-semibold text-ink text-[14px]">Legal bundle</p>
              <p className="text-[12px] text-ink/65 mt-1">
                Live Terms, Privacy, Storefront &amp; Transport agreements that back up this posture.
              </p>
            </Link>
            <Link
              to="/admin/pricing"
              className="rounded-xl border border-stone-200 bg-white p-4 hover:border-gold transition-colors"
            >
              <p className="font-semibold text-ink text-[14px]">Pricing &amp; fees</p>
              <p className="text-[12px] text-ink/65 mt-1">
                Source-of-truth for transaction fees referenced in insurance applications.
              </p>
            </Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
